import { CUT_LEGEND_KEY, CUT_LEGEND_SHORT_KEY, FILL_LEGEND_KEY, FILL_LEGEND_SHORT_KEY } from '@/lib/cutFillConvention';
import { netPerMetre, symmetricDomain } from '@/lib/stationVolumes/aggregate';
import { formatM3Legend, STATION_VOLUME_DISPLAY_DECIMALS } from '@/lib/stationVolumes/format';
import type { StationBin } from '@/lib/stationVolumes/types';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { useTranslation } from 'react-i18next';

/**
 * The 3D bands' key, INSIDE the dock header rather than floating over the
 * viewer.
 *
 * It used to be a `DockAwareChrome` overlay pinned bottom-right, where it
 * collided with the top-right analysis column (the cut/fill panel and the
 * deviation colour bar) and had its last line clipped. A key belongs next to
 * the control that turns the thing on: it appears the moment "3D bands" is
 * ticked and disappears with it, and it inherits the dock's `--sv-*` surface
 * instead of inventing its own dark card.
 *
 * The ramp is drawn from the SAME palette object the bands are (`useSvPalette`
 * → `SV_COLORS[theme]`), so the key cannot describe colours the corridor is
 * not painted in — including after a light/dark flip.
 */
export const StationBandsLegend = ({
  bins,
  lowCoverage,
  variant = 'inline',
}: {
  bins: StationBin[];
  lowCoverage: boolean;
  /**
   * `inline` (default): one 10 px row beside a toolbar toggle, the original
   * placement. `menu`: the dock's View ▾ popover (round-3 spec §B2), where the
   * key sits under its "3D bands" switch with a popover's width to spend — so
   * it is drawn at the dock's 11 px meta size with a full-width ramp, which the
   * 9–10 px toolbar copy never was (audit A9: "cannot be read").
   */
  variant?: 'inline' | 'menu';
}) => {
  const { t } = useTranslation('project');
  const palette = useSvPalette();
  // The p98 half-span the 3D ramp clamps at, using the same fixed precision as
  // the chart and table values it explains.
  const peak = symmetricDomain(bins.map(netPerMetre))[1];
  const [lo, mid, hi] = formatM3Legend([-peak, 0, peak], STATION_VOLUME_DISPLAY_DECIMALS);
  // A luminance step at each end, not hue alone: photocopied or seen by a
  // reader with a red/blue confusion, two mid-tones either side of a neutral
  // carry no polarity at all.
  const ramp =
    `linear-gradient(90deg, color-mix(in srgb, ${palette.cut} 62%, #000) 0%, ${palette.cut} 26%, ` +
    `${palette.zero} 50%, ${palette.fill} 74%, color-mix(in srgb, ${palette.fill} 58%, #fff) 100%)`;
  const lowSwatch = (
    <span
      aria-hidden
      className='inline-block h-2 w-4 rounded-[2px] outline outline-slate-400/50 dark:outline-stone-500/50'
      style={{ background: `repeating-linear-gradient(45deg, ${palette.zero} 0 2px, transparent 2px 4px)` }}
    />
  );

  if (variant === 'menu') {
    return (
      <div
        data-testid='station-bands-legend'
        className='flex flex-col gap-1 text-[11px] leading-4 text-slate-600 dark:text-stone-300'
      >
        <span className='sv-unit text-slate-500 dark:text-stone-400'>
          {t('stationVolumes.bands.legendTitle')} {t('stationVolumes.bands.unit')}
        </span>
        {/* The two named ends over the ramp's two ends, the ramp at the
            popover's full width, the ticks under it — so each figure sits
            under its colour and nothing has to share a narrow middle column. */}
        <div className='flex flex-col gap-0.5'>
          <span className='flex justify-between gap-3'>
            <span title={t(CUT_LEGEND_KEY)}>{t(CUT_LEGEND_SHORT_KEY)}</span>
            <span className='text-right' title={t(FILL_LEGEND_KEY)}>
              {t(FILL_LEGEND_SHORT_KEY)}
            </span>
          </span>
          <span aria-hidden className='h-2.5 w-full rounded-[2px]' style={{ background: ramp }} />
          <span className='sv-num flex justify-between tabular-nums' data-testid='sv-bands-legend-ticks'>
            <span>{lo}</span>
            <span>{mid}</span>
            <span>+{hi}</span>
          </span>
        </div>
        {lowCoverage && (
          <span className='flex items-center gap-1.5' data-testid='sv-bands-legend-low'>
            {lowSwatch}
            {t('stationVolumes.bands.lowCoverage')}
          </span>
        )}
        <span className='sr-only'>{t('stationVolumes.bands.legendHelp')}</span>
      </div>
    );
  }

  return (
    <div
      data-testid='station-bands-legend'
      // `basis-full` under 768 px drops the key onto the controls' own second
      // row instead of pushing the export menu off the edge; from `md` up it
      // sits inline, immediately after the checkbox it explains.
      className='order-last flex basis-full flex-wrap items-center justify-end gap-x-2 gap-y-1 text-[10px] leading-tight text-slate-600 md:order-none md:basis-auto dark:text-stone-300'
    >
      <span className='sv-unit opacity-70'>
        {t('stationVolumes.bands.legendTitle')} {t('stationVolumes.bands.unit')}
      </span>
      {/* Cut and fill are NAMED as well as coloured: the ramp alone is not an
          identity for a reader who cannot separate the two hues. The short
          convention string IS the name plus which way round the ground is
          ("Cut · above design"), so it stands alone — pairing it with the
          bare label printed the word twice. */}
      <span title={t(CUT_LEGEND_KEY)}>{t(CUT_LEGEND_SHORT_KEY)}</span>
      <span className='flex flex-col gap-0.5'>
        <span aria-hidden className='h-2 w-24 rounded-[2px]' style={{ background: ramp }} />
        <span className='sv-num flex w-24 justify-between tabular-nums' data-testid='sv-bands-legend-ticks'>
          <span>{lo}</span>
          <span>{mid}</span>
          <span>+{hi}</span>
        </span>
      </span>
      <span title={t(FILL_LEGEND_KEY)}>{t(FILL_LEGEND_SHORT_KEY)}</span>
      {lowCoverage && (
        <span className='flex items-center gap-1' data-testid='sv-bands-legend-low'>
          {lowSwatch}
          {t('stationVolumes.bands.lowCoverage')}
        </span>
      )}
      <span className='sr-only'>{t('stationVolumes.bands.legendHelp')}</span>
    </div>
  );
};
